import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import buildFiltersForHittingChart from '../filters/hittingChartsFilters.js';
import { HitData, PitchData, ResultData, PitchResult, PitchReprocessed, HitReprocessed, QualityOfContact } from '../enums/index.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';
import resultPairsSorter from '../sorters/resultPairsSorter.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';

const buildFiltersArray = function({
    // tagging context
    batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}) {
    let battingChartFilters = buildFiltersForHittingChart({
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    }, true);

    const filterOnlyWithLaunchV = filterOnlyHavingParameterSet(['hit'].concat(ResultData.getDataPath(HitData.LaunchV.key)));
    const filterOnlyWithExitSpeed = filterOnlyHavingParameterSet(['hit'].concat(ResultData.getDataPath(HitData.ExitSpeed.key)));
    const filterOnlyWithQualityOfContact = filterOnlyHavingParameterSet(['hit'].concat(ResultData.getDataPath(HitReprocessed.QualityOfContactR.key)));

    return battingChartFilters.concat(
        filterOnlyWithLaunchV, filterOnlyWithExitSpeed, filterOnlyWithQualityOfContact
    );
};

const mergeHitsWithoutPitchResult = function(pairsPerQualityOfContact) {
    const undefinedPitchResult = 'undefined';
    const defaultPitchResult = PitchResult.Unidentified.key;

    if (!pairsPerQualityOfContact.hasOwnProperty(undefinedPitchResult)){
        return;
    }

    const pairsWithoutPitchResult = pairsPerQualityOfContact[undefinedPitchResult];
    const pairsWithDefaultResult = pairsPerQualityOfContact[defaultPitchResult];

    if (typeof(pairsWithDefaultResult) === 'undefined'){
        pairsPerQualityOfContact[defaultPitchResult] = pairsWithoutPitchResult;
    }
    else{
        pairsPerQualityOfContact[defaultPitchResult] = pairsWithDefaultResult.concat(pairsWithoutPitchResult);
        pairsPerQualityOfContact[defaultPitchResult].sort(resultPairsSorter);
    }

    delete pairsPerQualityOfContact[undefinedPitchResult];
}

const getSeriesForQualityOfContactChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
) {
    const chartSeries = [];
    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    //const estimator = qualityOfContactEstimator || new QualityOfContactEstimator();
    const chartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithHitLaunchConditions = resultsProcessor.filterData(matchedResults, chartFilters);
    let pairsPerQualityOfContact = resultsProcessor.groupData(
        pairsWithHitLaunchConditions, [['hit'].concat(ResultData.getDataPath(HitReprocessed.QualityOfContactR.key))],
        [], true, QualityOfContact.Unidentified.key
    );

    // this is rare case but possible, so there was hit without pitch matched to it
    mergeHitsWithoutPitchResult(pairsPerQualityOfContact);
    for (let pitchResult in pairsPerQualityOfContact) {
        let resultsMapped = resultsProcessor.mapCollectionProperties(pairsPerQualityOfContact[pitchResult], [
            ['hit', ResultData.Id.key],
            ['hit', ResultData.PitchId.key],
            ['hit'].concat(ResultData.getDataPath(HitData.ExitSpeed.key)),
            ['hit'].concat(ResultData.getDataPath(HitData.LaunchV.key)),
        ]);

        const dataForPitchResult = new Series(pitchResult, 2);

        for (let i = 0; i < resultsMapped.length; i++) {
            dataForPitchResult.add(
                resultsMapped[i][ResultData.Id.key],
                resultsMapped[i][HitData.ExitSpeed.key],
                resultsMapped[i][HitData.LaunchV.key]
            );
        }

        chartSeries.push(dataForPitchResult);
    }

    return new ChartSeries(chartSeries, Object.keys(pairsPerQualityOfContact));
};

const listUniqueParametersForSprayChart = function(
    results, customResultsProcessor, {
        // tagging context
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
) {
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let chartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithHitLaunchConditions = resultsProcessor.filterData(matchedResults, chartFilters);
    
    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => ['pitch'].concat(x)),
        hitParameters.map(x => ['hit'].concat(x)),
        pitchToHitParamsToMerge,
        pairsWithHitLaunchConditions, // it is ok, hit and pitch filtering is included on parameters level
        pairsWithHitLaunchConditions,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getSeriesForQualityOfContactChart;

export {
    getSeriesForQualityOfContactChart, listUniqueParametersForSprayChart
};